import React from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { TooltipOptions } from "primereact/tooltip/tooltipoptions";
import { localeShortDateTime } from "../../../../app/datetimeUtils";
import { scoreReportEssayLink } from "../../../../app/links";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import { formatNumber, formatNumberOneDecimal } from "../../shared/reportUtil";
import {
	EssayTraitScoreViewModel,
	EssayTraitScoreWithNormsViewModel,
} from "./performanceReportApiSlice";

export function EssayTraitScoreTable(props: {
	className: string;
	scores: EssayTraitScoreWithNormsViewModel[];
	averages: EssayTraitScoreViewModel;
}) {
	const hasData = props.scores && props.scores.length > 0;

	const tooltipOptions: TooltipOptions = {
		position: "top",
		showDelay: 250,
		hideDelay: 0,
	};

	const dateTimeTemplate = (rowData: EssayTraitScoreWithNormsViewModel) => {
		return (
			<Link className="link" to={scoreReportEssayLink(rowData.essayId)}>
				{localeShortDateTime(rowData.date)}
			</Link>
		);
	};

	const textualEvidenceTemplate = (rowData: EssayTraitScoreWithNormsViewModel) => {
		return formatNumber(rowData.textualEvidenceScore);
	};

	const contentAccuracyTemplate = (rowData: EssayTraitScoreWithNormsViewModel) => {
		return formatNumber(rowData.contentAccuracyScore);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column
					footer="Overall Average"
					footerClassName="font-medium !text-right"
					colSpan={3}
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.developmentOfIdeasScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.organizationScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.styleScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.wordChoiceScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.sentenceStructureScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.conventionsScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.totalScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.textualEvidenceScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.averages.contentAccuracyScore)}
					footerClassName="font-medium !px-2"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<DataTable
			value={props.scores}
			dataKey="essayId"
			loading={props.scores == undefined}
			emptyMessage="No results found"
			stripedRows
			sortIcon={CustomSortIcon}
			className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
			footerColumnGroup={hasData && footerGroup}>
			<Column header="Date" body={dateTimeTemplate} sortable sortField="date"></Column>
			<Column header="Prompt" field="promptName" sortable></Column>
			<Column header="Drafts" field="draftCount"></Column>
			<Column
				header="Dev"
				field="developmentOfIdeasScore"
				className="!px-2"
				headerTooltip="Development of Ideas"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="Org"
				field="organizationScore"
				className="!px-2"
				headerTooltip="Organization"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="Style"
				field="styleScore"
				className="!px-2"
				headerTooltip="Style"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="WC"
				field="wordChoiceScore"
				className="!px-2"
				headerTooltip="Word Choice"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="Sent"
				field="sentenceStructureScore"
				className="!px-2"
				headerTooltip="Sentence Structure"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="Conv"
				field="conventionsScore"
				className="!px-2"
				headerTooltip="Conventions"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="Total"
				field="totalScore"
				className="!px-2"
				sortable
				headerTooltip="Total Score"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="Text"
				body={textualEvidenceTemplate}
				className="!px-2"
				headerTooltip="Textual Evidence"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
			<Column
				header="Cont"
				body={contentAccuracyTemplate}
				className="!px-2"
				headerTooltip="Content Accuracy"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2 min-w-12"></Column>
		</DataTable>
	);
}
